import { Link } from "react-router-dom"
import {
  earlyStartupsPath,
  growthStartupsPath,
  smallMediumBusinessesPath,
  privateEquityPath,
  acceleratorsPath,
} from "~/common/paths"
import { podsPath, teamPath, workDetailPath } from "~/common/paths"
import { solutionPresentations } from "~/content/solution-presentations"
import type { TestimonialId } from "~/content/testimonials"
import { HeadlineText } from "~/marketing/headline-text"
import { SourceContactEndcap } from "~/marketing/public-page"
import { TestimonialQuote } from "~/marketing/testimonial-quote"
import { WorkCover } from "~/marketing/work-cover"

const audiences = [
  {
    slug: "early-startups",
    to: earlyStartupsPath({}),
    title: "Get your first product into people’s hands.",
  },
  {
    slug: "growth-startups",
    to: growthStartupsPath({}),
    title: "Build the next thing your business needs.",
  },
  {
    slug: "small-medium-businesses",
    to: smallMediumBusinessesPath({}),
    title: "Make the work easier for your team and customers.",
  },
  {
    slug: "private-equity",
    to: privateEquityPath({}),
    title: "Move technology projects forward across your portfolio.",
  },
  {
    slug: "accelerators",
    to: acceleratorsPath({}),
    title: "Give founders practical help building their products.",
  },
] as const

export const SolutionsOverviewPage = () => (
  <div className="service-page solution-page">
    <section className="service-section service-hero">
      <div className="service-container solution-overview-hero">
        <div className="service-promise">
          <p className="service-eyebrow">SOLUTIONS</p>
          <h1 className="marketing-page-title marketing-split-title">
            <span className="headline-beat">What are you working on?</span>{" "}
            <span className="headline-beat">
              Let’s figure out{" "}
              <span className="home-emphasis home-highlight">what comes next.</span>
            </span>
          </h1>
          <p className="service-intro">
            A first product, a growing business, or a portfolio of companies. Find the help that
            fits the work in front of you.
          </p>
          <a className="service-cta" href="#service-contact">
            Talk about your project
          </a>
        </div>
        <img
          className="service-mascot"
          src="/images/solutions/solutions.webp"
          width={540}
          height={520}
          alt=""
          fetchPriority="high"
        />
      </div>
    </section>
    <section className="service-section service-brown">
      <div className="service-container">
        <div className="service-introduction">
          <h2>Where are you starting?</h2>
          <p className="service-intro">
            Choose the situation closest to yours. We’ll work out the right starting point together.
          </p>
        </div>
        <nav className="solution-directory" aria-label="Find your solution">
          {audiences.map(({ slug, to, title }) => (
            <Link key={slug} to={to}>
              <div>
                <p className="service-eyebrow">{solutionPresentations[slug].label}</p>
                <h3>{title}</h3>
              </div>
              <span aria-hidden="true">→</span>
            </Link>
          ))}
        </nav>
      </div>
    </section>
    <section className="service-section">
      <div className="service-container">
        <h2>
          Different businesses.
          <br />
          Useful things built.
        </h2>
        <div className="solution-selected-work">
          {(
            [
              ["the-forem", "The Forem", "From a pitch deck to a product people could try."],
              [
                "pearland",
                "Pearland Brokerage",
                "Online renewals and payments connected to daily operations.",
              ],
              [
                "quadrant",
                "Quadrant Private Capital",
                "Portfolio projections and client visibility in one portal.",
              ],
            ] as const
          ).map(([id, name, summary]) => (
            <Link key={id} to={workDetailPath({ slug: id })}>
              <WorkCover id={id} />
              <h3>{name}</h3>
              <p>{summary}</p>
              <span>Read the story →</span>
            </Link>
          ))}
        </div>
      </div>
    </section>
    <section className="service-section service-brown">
      <div className="service-container service-detail-grid">
        <h2>
          Start with the work.
          <br />
          Choose the support.
        </h2>
        <div className="service-entries">
          <div>
            <h3>Work through a question</h3>
            <p>A focused conversation, workshop, or assessment.</p>
          </div>
          <div>
            <h3>Define the first build</h3>
            <p>Sprint Zero brings the product, design, and technical plan together.</p>
          </div>
          <div>
            <h3>Keep building</h3>
            <p>A Pod gives you an ongoing team at a pace you choose.</p>
          </div>
        </div>
      </div>
    </section>
    <div id="service-contact" className="service-contact">
      <SourceContactEndcap
        copy={{
          heading: "Tell us where\nthings stand.",
          body: "Bring the idea, the project, or the problem. We’ll talk through a useful next step.",
        }}
        showNewsletter={false}
      />
    </div>
  </div>
)

const solutionTestimonials = {
  "early-startups": "yosh-miller",
  "growth-startups": "daniela-perdomo",
  "small-medium-businesses": "soraya-darabi",
  "private-equity": "nick-kroetz",
  "accelerators": "bre-pettis",
} as const satisfies Record<keyof typeof solutionPresentations, TestimonialId>

export const SolutionDetailPage = ({ page }: { page: keyof typeof solutionPresentations }) => {
  const content = solutionPresentations[page]

  return (
    <div className="service-page solution-page">
      <section className="service-section service-hero">
        <div className="service-container service-equal-grid">
          <div className="service-promise">
            <p className="service-eyebrow">{content.label}</p>
            <h1 className="marketing-page-title marketing-split-title">
              <span className="headline-beat">{content.lead}</span>{" "}
              <span className="headline-beat">
                <HeadlineText text={content.headline} accent={content.headlineAccent} />
              </span>
            </h1>
            <p className="service-intro">{content.body}</p>
            <a className="service-cta" href="#service-contact">
              Talk about your project
            </a>
          </div>
          <img
            className="service-mascot"
            src={`/images/solutions/${content.slug}.webp`}
            width={620}
            height={560}
            alt=""
            fetchPriority="high"
          />
        </div>
      </section>
      <section className="service-section service-brown">
        <div className="service-container">
          <div className="service-detail-grid">
            <h2>{content.outcome}</h2>
            <p className="service-intro">{content.outcomeBody}</p>
          </div>
          <div className="service-outcomes">
            {content.outputs.map(([heading, body]) => (
              <div key={heading}>
                <h3>{heading}</h3>
                <p>{body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      <section className="service-section">
        <div className="service-container service-equal-grid service-proof">
          {page === "accelerators" ? (
            <div className="solution-origin">
              <strong>7 products.</strong>
              <strong>14 months.</strong>
              <p>Built together at Q Labs.</p>
            </div>
          ) : (
            <WorkCover id={content.proofId} />
          )}
          <div className="service-introduction">
            <p className="service-eyebrow">{content.proofName}</p>
            <h2>{content.proof}</h2>
            <p>{content.proofBody}</p>
            <Link
              to={
                page === "accelerators" ? teamPath({}) : workDetailPath({ slug: content.proofId })
              }
            >
              {content.proofLink}
            </Link>
          </div>
        </div>
      </section>
      <section className="service-section service-brown">
        <div className="service-container">
          <div className="service-detail-grid">
            <div className="service-introduction">
              <h2>{content.process}</h2>
              <p className="service-intro">{content.processBody}</p>
            </div>
            <ol className="service-steps">
              {content.steps.map(([heading, body]) => (
                <li key={heading}>
                  <div>
                    <h3>{heading}</h3>
                    <p>{body}</p>
                  </div>
                </li>
              ))}
            </ol>
          </div>
          <div className="service-workflow">
            <p>{content.artifactTitle}</p>
            <ol>
              {content.artifact.map((stage) => (
                <li key={stage}>
                  <span className="service-number" aria-hidden="true">
                    →
                  </span>
                  <strong>{stage}</strong>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>
      <TestimonialQuote id={solutionTestimonials[page]} />
      <section className="service-section">
        <div className="service-container">
          <div className="service-detail-grid">
            <div className="service-introduction">
              <h2>{content.start}</h2>
              <p className="service-intro">{content.startBody}</p>
            </div>
            <div className="service-entries">
              {content.entry.map(([heading, body]) => (
                <div key={heading}>
                  <h3>{heading}</h3>
                  <p>{body}</p>
                </div>
              ))}
            </div>
          </div>
          <div className="service-bridge">
            <p>{content.bridge}</p>
            <Link to={podsPath({})}>See how Pods work →</Link>
          </div>
        </div>
      </section>
      <div id="service-contact" className="service-contact">
        <SourceContactEndcap
          copy={{ heading: content.close, body: content.closeBody }}
          showNewsletter={false}
        />
      </div>
    </div>
  )
}
